import NextLink from 'next/link';
import React from 'react';

import { Button } from '../../components/buttons/Button';
import { Link } from '../../components/buttons/Link';
import { IconName } from '../../components/icons/Icons';
import { DecorationWaves } from '../../components/module/DecorationWaves';
import { Wrapper } from '../../components/module/Wrapper';

import LogoImage from '../../public/logo.svg';

export type FooterProps = {
  socials: { label?: string; href?: string; icon: IconName }[];
  links: { title?: string; items: { label?: string; href?: string }[] }[];
  copyright?: string;
  children?: React.ReactElement | React.ReactNode;
};

export const Footer = ({
  socials,
  links,
  children,
  copyright = '©',
}: FooterProps) => {
  return (
    <div className="px-[6px] relative pb-[6px]">
      <div className="absolute bottom-[6px] left-[6px] w-[25vw] h-[35px] md:h-[50px] z-10">
        <DecorationWaves
          verticalAlign="bottom"
          horizontalAlign="left"
          color="navy-900"
          points={3}
        />
      </div>
      <div className="absolute bottom-[6px] right-[6px] w-[25vw] h-[35px] md:h-[50px] z-10">
        <DecorationWaves
          verticalAlign="bottom"
          horizontalAlign="right"
          color="navy-900"
          points={3}
        />
      </div>

      <Wrapper theme={{ background: 'navy-800', text: 'white' }}>
        <div className="flex flex-col xl:flex-row gap-8 border-black border-opacity-10 pt-16">
          <div className="flex flex-col gap-8 pr-16">
            <div className="h-10">
              <NextLink href="/">
                <a>
                  <svg
                    className="h-full"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    shapeRendering="geometricPrecision"
                    viewBox="0 0 249 80"
                  >
                    <g clipPath="url(#a)">
                      <path
                        d="M4 32.65c-2.57 13.9-.14 27 10.65 37C23 77.44 34.38 80.37 45.6 80c9.45-.35 19.15-3.53 25.58-10.49a33.361 33.361 0 0 0 7.75-15.7 49.632 49.632 0 0 0 .85-14.94c-.55-6-3-10.67-6.65-15.32A49.623 49.623 0 0 0 61.5 12.81C55.13 8.55 47.6 5 40 3.6a32.861 32.861 0 0 0-5.78-.53C17.84 3.08 7 16.41 4 32.65Z"
                        fill="#FFD51D"
                      />
                      <path
                        d="M78.19 27.26a50.476 50.476 0 0 0-6.72-13.56c-3.46-5-8-7.83-13.41-10.07A48.87 48.87 0 0 0 42.64.15C35-.35 26.66.34 19.42 3 .64 9.95-3.49 29.33 2.64 46.92 7.34 60.44 16 70.7 30.33 74c11.19 2.56 22.48-.64 32-6.72 8-5.12 14.85-12.87 17-22.26a34.36 34.36 0 0 0-1.11-17.74"
                        fill="#74D9D7"
                      />
                      <path
                        d="M66.64 13.35a31.72 31.72 0 0 0-14.4-8.46 46.93 46.93 0 0 0-14.14-1.8c-5.73.13-10.34 2.2-15 5.35a47.452 47.452 0 0 0-11 10.42C7.67 24.69 3.75 31.65 2 38.81c-4.64 18.58 8.25 32.06 25.21 36.46 13 3.38 25.64 1.93 35.86-7.75 8-7.54 11.52-18.2 11.87-29 .3-9.07-2.08-18.58-8.27-25.2"
                        fill="#F459A5"
                      />
                      <path
                        d="M79.579 37.58c-1-5.19-3.41-10-5.88-14.62-3.53-6.69-7.33-13.56-13.05-18.61-5.92-5.21-14.16-5.07-21.37-3.05-6.52 1.83-14.7 4.12-19.82 8.62-4.82 4.24-7.52 11.12-8.84 17.3-2.51 11.78-5.67 29.92 4.51 38.95 5.86 5.19 14 7.38 21.63 7.65 8.08.29 18.33-2.4 26.82-7.48 10.52-6.3 18.35-16.26 16-28.77"
                        fill="#5F6CFF"
                      />
                      <path
                        d="M76.11 29.23a29.3 29.3 0 0 0-8-13.11 43.13 43.13 0 0 0-10.68-7.59C52.74 6.16 48.12 5.84 43 6.38a43.21 43.21 0 0 0-13.33 3.72 51.26 51.26 0 0 0-16.79 11.78c-11.72 13.07-7.09 29.6 4.74 40.51 9.1 8.39 19.91 12.67 32.33 9.25C59.64 69 67.07 61.86 72 53.26c4.13-7.23 6.29-16 4.12-24"
                        fill="#000"
                      />
                      <path
                        d="M55.289 35.18c-1.61-2.21-4.55-5.34-7.36-3a5.609 5.609 0 0 1-1.93 1.24c-1.64.49-2.45-1.18-3.17-2.32a19.108 19.108 0 0 0-3.29-4.55 3.54 3.54 0 0 0-1.54-.86 3.22 3.22 0 0 0-2.11.38c-3.68 1.92-6.43 5.36-8.51 8.86-2.08 3.5-4.29 7.93-4.08 12.22.06 1.18.17 2.78 1.12 3.63 1.32 1.16 3.2-.06 4.27-1l.7-.62c2.18-2 4.22-4.11 7-5.25a1.36 1.36 0 0 1 1.34 0c1.29 1-.54 3.92-.24 5.3a2.76 2.76 0 0 0 2.72 2.09c2.15 0 4.1-1.89 5.82-3 1.18-.74 2.85-1.93 4.32-1.85 2 .11 1 2.33 1.63 3.47a2.57 2.57 0 0 0 1.89 1.08 4.94 4.94 0 0 0 5.87-5.25c-.17-3.91-2.19-7.51-4.45-10.62"
                        fill="#fff"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M212.611 23v25.251c0 2.253.627 4.012 1.88 5.279 1.268 1.267 3.049 1.9 5.342 1.9h2.329v-5.89h-.833c-1.282 0-1.923-.69-1.923-2.07V23h-6.795Zm-67.156 21.134c0-3.59.969-6.503 2.906-8.74 1.952-2.239 4.48-3.358 7.585-3.358 1.61 0 2.963.33 4.06.992 1.111.648 1.93 1.373 2.457 2.175h.363v-2.597h6.774V55.43h-6.731v-2.596h-.363c-.2.323-.456.64-.769.95-.314.31-.727.647-1.24 1.013-.498.352-1.132.64-1.901.866a8.642 8.642 0 0 1-2.479.338c-3.191 0-5.769-1.084-7.735-3.252-1.951-2.167-2.927-5.039-2.927-8.614Zm6.859-.127c0 1.774.491 3.21 1.474 4.307.983 1.084 2.272 1.626 3.868 1.626 1.595 0 2.877-.556 3.846-1.668.968-1.126 1.453-2.547 1.453-4.265 0-1.787-.499-3.216-1.496-4.286-.997-1.083-2.279-1.625-3.846-1.625-1.567 0-2.842.549-3.825 1.646-.983 1.098-1.474 2.52-1.474 4.265Zm20.385-11.4h7.051l3.055 14.652h.364l4.359-14.653h6.623l4.359 14.653h.363l3.056-14.653h7.051l-6.132 22.824h-7.436l-4.423-13.914h-.256l-4.466 13.914h-7.436l-6.132-22.824Zm52.156 11.527c0-3.59.969-6.503 2.906-8.74 1.952-2.239 4.48-3.358 7.586-3.358 1.609 0 2.962.33 4.059.992 1.111.648 1.93 1.373 2.457 2.175h.364v-2.597H249V55.43h-6.731v-2.596h-.363c-.199.323-.456.64-.769.95-.313.31-.726.647-1.239 1.013-.499.352-1.133.64-1.902.866a8.64 8.64 0 0 1-2.478.338c-3.191 0-5.77-1.084-7.735-3.252-1.952-2.167-2.928-5.039-2.928-8.614Zm6.859-.127c0 1.774.492 3.21 1.475 4.307.982 1.084 2.272 1.626 3.867 1.626 1.595 0 2.877-.556 3.846-1.668.969-1.126 1.453-2.547 1.453-4.265 0-1.787-.499-3.216-1.496-4.286-.997-1.083-2.279-1.625-3.846-1.625-1.567 0-2.842.549-3.824 1.646-.983 1.098-1.475 2.52-1.475 4.265ZM135.339 56h7.04V41.216c0-.968-.088-1.95-.264-2.948a9.396 9.396 0 0 0-.968-2.86c-.469-.88-1.158-1.584-2.068-2.112-.909-.557-2.097-.836-3.564-.836-1.818 0-3.461.396-4.928 1.188-1.437.763-2.596 1.863-3.476 3.3-.293-1.203-.953-2.244-1.98-3.124-1.026-.91-2.493-1.364-4.4-1.364-1.936 0-3.637.381-5.104 1.144a8.505 8.505 0 0 0-3.432 3.168V32.9h-6.336V56h7.04V42.756c.411-1.203 1.071-2.185 1.98-2.948.91-.763 1.819-1.144 2.728-1.144.88 0 1.599.352 2.156 1.056.558.675.836 1.775.836 3.3V56h7.04V42.756c.382-1.203 1.027-2.185 1.936-2.948.91-.763 1.834-1.144 2.772-1.144.88 0 1.599.352 2.156 1.056.558.704.836 1.804.836 3.3V56Z"
                        fill="#fff"
                      />
                    </g>
                    <defs>
                      <clipPath id="a">
                        <path fill="#fff" d="M0 0h249v80H0z" />
                      </clipPath>
                    </defs>
                  </svg>
                </a>
              </NextLink>
            </div>

            {children}
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 flex-grow">
            {links?.map(({ title, items }) => (
              <div className="mb-6" key={title || JSON.stringify(items)}>
                <h2 className="uppercase font-medium text-sm mb-4 opacity-80">
                  {title}
                </h2>

                <ul className="flex flex-col gap-4">
                  {items?.map(({ label, href }) => (
                    <li key={label} className="opacity-60">
                      <Link href={href} className="block hover:underline">
                        {label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {copyright && (
          <div className="border-t border-black border-opacity-10 xl:text-center mt-12 py-8 text-md">
            {Boolean(socials?.length) && (
              <ul className="flex flex-col md:flex-row md:gap-4 md:items-center">
                <li className="mr-10 xl:mr-0 text-left align-left xl:pl-[220px]">
                  <ul>
                    <li>
                      <address className="not-italic">HQ: Dublin, Ireland.</address>
                    </li>
                    <li>
                      <address className="not-italic">
                        UK: 1 Customs Wharf Leith, Edinburgh EH6 6AL.
                      </address>
                    </li>
                  </ul>
                  <p className="pt-2">{copyright}</p>
                </li>

                <li className="flex flex-row gap-8 md:gap-4 pt-8 md:pt-0 lg:pl-[100px] xl:pl-[170px]">
                  {socials?.map(({ label, href, icon }) => (
                    <li key={label} className="opacity-75">
                      <Button
                        href={href}
                        ariaLabel={label}
                        label=""
                        theme={{
                          background: 'navy-900',
                          text: 'white',
                        }}
                        icon={icon}
                        compact
                        plain
                      />
                    </li>
                  ))}
                </li>
              </ul>
            )}
          </div>
        )}
      </Wrapper>
    </div>
  );
};
